/**
 * Manages the "More" mega menu's left category list / right swappable panel
 * interaction. Hovering, clicking, or focusing a tab activates its matching
 * panel; all other tabs/panels are deactivated.
 */
class MegaMenuTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('pointerenter', this.#onTabPointerEnter, true);
    this.addEventListener('click', this.#onTabClick);
    this.addEventListener('focusin', this.#onTabFocusIn);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.#onTabPointerEnter, true);
    this.removeEventListener('click', this.#onTabClick);
    this.removeEventListener('focusin', this.#onTabFocusIn);
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #findTab(event) {
    if (!(event.target instanceof Element)) return null;
    return event.target.closest('[data-more-tab]');
  }

  /** @param {PointerEvent} event */
  #onTabPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    const tab = this.#findTab(event);
    if (tab) this.#activate(tab);
  };

  /** @param {MouseEvent} event */
  #onTabClick = (event) => {
    const tab = this.#findTab(event);
    if (tab) this.#activate(tab);
  };

  /** @param {FocusEvent} event */
  #onTabFocusIn = (event) => {
    const tab = this.#findTab(event);
    if (tab) this.#activate(tab);
  };

  /** @param {HTMLElement} tab */
  #activate(tab) {
    const key = tab.dataset.moreTab;
    if (!key || tab.classList.contains('is-active')) return;

    this.querySelectorAll('[data-more-tab]').forEach((el) => {
      const isActive = el === tab;
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-selected', isActive.toString());
    });

    this.querySelectorAll('[data-more-panel]').forEach((el) => {
      el.classList.toggle('is-active', /** @type {HTMLElement} */ (el).dataset.morePanel === key);
    });

    // Let header-menu.js know the submenu content size may have changed.
    this.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('mega-menu-tabs')) {
  customElements.define('mega-menu-tabs', MegaMenuTabs);
}
